import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as moment from 'moment';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  take,
  takeUntil,
} from 'rxjs/operators';
import { getFracName } from 'src/app/ui-components/pipes/frac-name.pipe';
import { BoxPickupSite, Frac, FracClusterTruckingVendor } from '~lmo/models/frac.model';
import { GroupedMeshes, Mesh } from '~lmo/models/mesh.model';
import { FracMine } from '~lmo/models/mine.model';
import { DeadheadLinkableOrder, OrderCreate } from '~lmo/models/order.model';
import { Report } from '~lmo/models/report.model';
import { LmoOrdersService } from '~lmo/services';
import { LmoFracFcpoService } from '~lmo/services/lmo-frac-fcpo.service';
import { LmoFracsService } from '~lmo/services/lmo-fracs.service';
import { LmoReportsService } from '~lmo/services/lmo-reports.service';
import { UserService } from '~services/user.service';
import { fuse } from '~utilities/fuse';
import { FracStorage } from '~v2Models/frac.model';
import { FracClusterPurchaseOrder, PurchaseOrder } from '~v2Models/purchaseOrders.model';
import { TrailerType } from '~v2Models/well.model';

const searchOptions: Fuse.FuseOptions<{ id: number; name: string }> = {
  distance: 100,
  keys: ['name'],
  location: 0,
  maxPatternLength: 9,
  minMatchCharLength: 1,
  shouldSort: true,
  threshold: 0.2,
};

interface NewOrderForm {
  loader: FracMineWithPoInformation;
  meshId: number;
  numberOfTrucks: number;
  pickupTime: Date;
  staggerMinutes?: number;
  truckingVendorId: number;
  trailerTypeId: number;
  createBoxPickup: boolean;
  firstStop: boolean;
  boxPickupSiteId: BoxPickupSite;
  outOfRouteMiles: number;
  outOfRouteRateCents: number;
}

interface FracMineWithPoInformation extends FracMine {
  fcpo: FracClusterPurchaseOrder;
  downReport?: Report;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'sa-lmo-orders-new',
  styleUrls: ['./lmo-orders-new.component.scss'],
  templateUrl: './lmo-orders-new.component.html',
})
export class LmoOrdersNewComponent implements OnInit, OnDestroy {
  public destroy$$ = new Subject();
  @ViewChild('exceedsAllocations', { static: true }) private exceedsAllocations: TemplateRef<any>;
  public confirmOverAllocationOrder: MatDialogRef<any, any>;
  public newOrderForm: FormGroup;
  public currentFrac$: Observable<Frac>;
  public currentFracName$: Observable<string>;
  public meshesInGroup$: Observable<GroupedMeshes[]>;
  public fracMines$: Observable<FracMineWithPoInformation[]>;
  public truckingVendors$: Observable<FracClusterTruckingVendor[]>;
  public lohiSites$: Observable<{ id: number; name: string }[]>;
  public searchForm = new FormControl(null);
  private deadheadLinkableOrders$$ = new BehaviorSubject<DeadheadLinkableOrder[]>([]);
  public deadheadLinkableOrders$ = this.deadheadLinkableOrders$$.pipe(shareReplay(1));
  public deadHeadSearchForm = new FormControl(null);
  public trailerTypes$: Observable<TrailerType[]>;
  public boxPickupSites$: Observable<BoxPickupSite[]>;
  public selectedBoxPickupSite$: Observable<BoxPickupSite>;

  private get formValue(): NewOrderForm {
    return this.newOrderForm.value;
  }

  constructor(
    private orderService: LmoOrdersService,
    private fracService: LmoFracsService,
    private users: UserService,
    private fb: FormBuilder,
    private matDialogRef: MatDialogRef<LmoOrdersNewComponent, any>,
    private snackbar: MatSnackBar,
    private fcpoService: LmoFracFcpoService,
    private matDialog: MatDialog,
    private reportService: LmoReportsService,
  ) {}

  public ngOnInit() {
    this.currentFrac$ = this.fracService.currentFrac$.pipe(filter((frac) => !!frac));
    this.currentFracName$ = this.currentFrac$.pipe(map(getFracName));
    this.newOrderForm = this.fb.group({
      boxPickupSiteId: [null, [Validators.required]],
      createBoxPickup: [false, [Validators.required]],
      firstStop: [false, [Validators.required]],
      loader: this.fb.control({ value: null, disabled: true }, [Validators.required]),
      meshId: ['', [Validators.required]],
      numberOfTrucks: ['', [Validators.required]],
      outOfRouteMiles: [null, [Validators.required]],
      outOfRouteRateCents: [200, [Validators.required]],
      pickupTime: [null],
      staggerMinutes: this.fb.control({ value: 0, disabled: true }),
      trailerTypeId: [null],
      truckingVendorId: ['', [Validators.required]],
    });
    this.setupLoHiSiteSearch();
    this.setupBoxPickupSiteSearch();
    this.setupFormDisableAndEnableListeners();
    this.setupListenersForFormOptions();
    this.listenToDeadheadSearch();
  }

  public ngOnDestroy() {
    this.destroy$$.next();
    this.destroy$$.unsubscribe();
  }

  public isVortoUser() {
    return this.users.isVortoUser() || this.users.isShaleappsEmail();
  }

  public trackByFCPOId(fcpo: FracMineWithPoInformation) {
    return fcpo;
  }

  public trackByVendorId(truckingVendor: FracClusterTruckingVendor) {
    return truckingVendor.id;
  }

  public trackByTrailerType(index: number, item: TrailerType): number {
    return item.id;
  }

  public trackByLohiSiteId(siteIdName: { id: number; name: string }) {
    return siteIdName.id;
  }

  public trackByBoxPickupSiteId(siteIdName: { id: number; name: string }) {
    return siteIdName.id;
  }

  public trackByLinkableId(linkableOrder: DeadheadLinkableOrder) {
    return linkableOrder.id;
  }

  public placeOrder() {
    if (this.newOrderForm.invalid) {
      return;
    }
    this.wouldBeMaxedOut$()
      .pipe(
        switchMap((wouldBeMaxedOut) => {
          if (!wouldBeMaxedOut) {
            return this.currentFrac$;
          } else {
            this.confirmOverAllocationOrder = this.matDialog.open(this.exceedsAllocations);
            return this.confirmOverAllocationOrder.afterClosed().pipe(
              switchMap((orderAnyway) => {
                if (orderAnyway) {
                  return this.currentFrac$;
                }
                return of(null);
              }),
            );
          }
        }),
        take(1),
        map((frac) => {
          if (!frac) {
            return null;
          }
          const formValue = this.formValue;
          const partialOrders: OrderCreate[] = [];
          const createTimeString = new Date().toISOString();
          const startDate = formValue.pickupTime !== null ? moment(formValue.pickupTime) : moment(createTimeString);
          for (let i = 0; i < formValue.numberOfTrucks; i++) {
            const minutesToAdd = i * (formValue.staggerMinutes || 0);
            const startTimestamp = startDate
              .clone()
              .add(minutesToAdd, 'minutes')
              .toISOString();
            const partialOrder: OrderCreate = {
              createBoxPickupInfo: formValue.createBoxPickup
                ? {
                    boxPickupSiteId: formValue.boxPickupSiteId.siteId,
                    firstStop: this.newOrderForm.get('firstStop').value,
                    outOfRouteMiles: this.newOrderForm.get('outOfRouteMiles').value,
                    outOfRouteRateCents: this.newOrderForm.get('outOfRouteRateCents').value,
                  }
                : null,
              createTimestamp: createTimeString,
              fracId: frac.id,
              mesh: {
                id: formValue.meshId,
              },
              mine: {
                id: formValue.loader.id,
              },
              purchaseOrder: {
                id: formValue.loader.fcpo.purchaseOrder.id,
              },
              startTimestamp,
              trailerType: {
                trailerTypeId: formValue.trailerTypeId,
              },
              vendor: {
                id: formValue.truckingVendorId,
              },
            };
            partialOrders.push(partialOrder);
          }
          return partialOrders;
        }),
        switchMap((partialOrders) => {
          if (!partialOrders) {
            return of(false);
          }
          return this.orderService.placeOrders$(partialOrders);
        }),
      )
      .subscribe((wasCreated) => {
        if (wasCreated) {
          this.snackbar.open('Orders successfully created', null, { duration: 5000 });
          this.matDialogRef.close();
        }
      });
  }

  public isMaxedOut(po: PurchaseOrder): boolean {
    if (!po) {
      return false;
    }
    return (po.orderedToday || 0) > po.maximumDailyAllocation || (po.ordered || 0) + (po.pickedUp || 0) > po.allocation;
  }

  public wouldBeMaxedOut$(): Observable<boolean> {
    return combineLatest(this.getDailyAllocationOverageAmount$(), this.getTotalAllocationOverageAmount$()).pipe(
      map(([daily, total]) => {
        return daily > 0 || total > 0;
      }),
    );
  }

  public getDailyAllocationOverageAmount$(): Observable<number> {
    return combineLatest(
      this.selectedStorageAndFCPO$(),
      this.newOrderForm
        .get('numberOfTrucks')
        .valueChanges.pipe(startWith(this.newOrderForm.get('numberOfTrucks').value)),
    ).pipe(
      map(([[fracStorage, fcpo], orderCount]) => {
        if (!fracStorage || !fcpo || !orderCount) {
          return 0;
        }
        const orderedAmount = fracStorage.loadWeight * orderCount;
        const amountLeftAfter =
          fcpo.purchaseOrder.maximumDailyAllocation - ((fcpo.purchaseOrder.orderedToday || 0) + orderedAmount);
        return amountLeftAfter < 0 ? Math.abs(amountLeftAfter) : 0;
      }),
    );
  }

  public getTotalAllocationOverageAmount$(): Observable<number> {
    return combineLatest(
      this.selectedStorageAndFCPO$(),
      this.newOrderForm
        .get('numberOfTrucks')
        .valueChanges.pipe(startWith(this.newOrderForm.get('numberOfTrucks').value)),
    ).pipe(
      map(([[fracStorage, fcpo], orderCount]) => {
        if (!fracStorage || !fcpo || !orderCount) {
          return 0;
        }
        const orderedAmount = fracStorage.loadWeight * orderCount;
        const amountLeftAfter =
          fcpo.purchaseOrder.allocation -
          ((fcpo.purchaseOrder.ordered || 0) + (fcpo.purchaseOrder.pickedUp || 0) + orderedAmount);
        if (amountLeftAfter < 0) {
          return Math.abs(amountLeftAfter);
        }
        return 0;
      }),
    );
  }

  private setupLoHiSiteSearch() {
    this.lohiSites$ = combineLatest([
      this.searchForm.valueChanges.pipe(startWith(null as any), distinctUntilChanged()),
      this.orderService.loHiSitesForBoxPickup$.pipe(map(fuse(searchOptions))),
    ]).pipe(
      map(([searchValue, sites]) => {
        if (!searchValue) {
          return sites.data;
        } else {
          return sites.fuse.search(searchValue) as any;
        }
      }),
    );
  }

  private setupBoxPickupSiteSearch() {
    this.boxPickupSites$ = combineLatest([
      this.searchForm.valueChanges.pipe(startWith(null as any), distinctUntilChanged()),
      this.fracService.boxPickupSites$.pipe(map(fuse(searchOptions))),
    ]).pipe(
      map(([searchValue, sites]) => {
        if (!searchValue) {
          this.setupBoxPickupSelectionListener();
          return sites.data;
        } else {
          return sites.fuse.search(searchValue) as any;
        }
      }),
    );
  }

  private setupBoxPickupSelectionListener() {
    this.newOrderForm.get('boxPickupSiteId').valueChanges.subscribe((site) => {
      if (site) {
        this.newOrderForm.get('outOfRouteMiles').setValue(site.outOfRouteMiles);
        this.newOrderForm.get('outOfRouteRateCents').setValue(site.outOfRouteRateCents);
      }
    });
  }

  private selectedStorageAndFCPO$(): Observable<[FracStorage, FracClusterPurchaseOrder]> {
    return combineLatest(
      this.currentFrac$,
      this.newOrderForm.get('meshId').valueChanges.pipe(startWith(this.newOrderForm.get('meshId').value)),
      this.newOrderForm.get('loader').valueChanges.pipe(startWith(this.newOrderForm.get('loader').value)),
      this.fcpoService.currentFracFcpos$,
    ).pipe(
      map(([frac, meshId, loader, fcpos]: [Frac, number, FracMineWithPoInformation, FracClusterPurchaseOrder[]]) => {
        const fracStorage = frac.fracStorages.find((storage) => storage.mesh.id === meshId);
        const fcpo = fcpos.find((f) => f.id === loader.fcpo.id);
        return [fracStorage, fcpo] as [FracStorage, FracClusterPurchaseOrder];
      }),
    );
  }

  private setupFormDisableAndEnableListeners() {
    this.newOrderForm
      .get('meshId')
      .valueChanges.pipe(distinctUntilChanged())
      .subscribe((meshId) => {
        if (!meshId) {
          this.newOrderForm.get('loader').disable();
        } else {
          this.newOrderForm.get('loader').enable();
        }
      });

    this.newOrderForm
      .get('numberOfTrucks')
      .valueChanges.pipe(distinctUntilChanged())
      .subscribe((numberOfTrucks: number) => {
        if (numberOfTrucks > 1) {
          this.newOrderForm.get('staggerMinutes').enable();
        } else {
          this.newOrderForm.get('staggerMinutes').disable();
        }
      });

    this.newOrderForm.get('firstStop').disable();
    this.newOrderForm.get('boxPickupSiteId').disable();
    this.newOrderForm.get('outOfRouteMiles').disable();
    this.newOrderForm.get('outOfRouteRateCents').disable();
    this.newOrderForm
      .get('createBoxPickup')
      .valueChanges.pipe(distinctUntilChanged())
      .subscribe((createBoxPickup) => {
        if (createBoxPickup) {
          this.newOrderForm.get('boxPickupSiteId').enable();
          this.newOrderForm.get('firstStop').enable();
        } else {
          this.newOrderForm.get('boxPickupSiteId').disable();
          this.newOrderForm.get('firstStop').disable();
        }
      });
  }

  private setupListenersForFormOptions() {
    this.meshesInGroup$ = this.currentFrac$.pipe(
      map((frac) => {
        const uniqueMeshRecord = (frac.fracStorages || []).reduce((meshRecord, storage) => {
          meshRecord[storage.id] = {
            meshName: storage.mesh.type,
            meshes: [storage.mesh],
            storageId: storage.id,
          } as GroupedMeshes;
          return meshRecord;
        }, {});

        for (const fracStorage of frac.fracStorages) {
          for (const mesh of fracStorage.supplementalMeshes || []) {
            const supplementalMesh: Mesh = {
              id: mesh.meshId,
              isSupplemental: true,
              preferredOrder: 0,
              type: mesh.meshName,
            };
            uniqueMeshRecord[fracStorage.id].meshes.push(supplementalMesh);
          }
        }
        return Object.keys(uniqueMeshRecord).map((key) => uniqueMeshRecord[key]);
      }),
    );

    this.fracMines$ = combineLatest(
      this.newOrderForm.get('meshId').valueChanges.pipe(startWith(-1)),
      this.currentFrac$,
      this.fcpoService.currentFracFcpos$,
      this.reportService.reports$,
    ).pipe(
      map(([selectedMesh, currentFrac, fcpos, reports]) => {
        const validMineIds = fcpos
          .map((fcpo) => fcpo.purchaseOrder)
          .filter((po) => po.meshId === selectedMesh)
          .map((po) => po.mineId);
        const fracMines = [...(currentFrac.fracMines || [])];
        const fracMinesWithPoInfo: FracMineWithPoInformation[] = [];
        fracMines.forEach((fracMine) => {
          const mineFCPOs = fcpos.filter(
            (fcpo) =>
              fcpo.purchaseOrder &&
              fcpo.purchaseOrder.mineId === fracMine.id &&
              fcpo.purchaseOrder.meshId === selectedMesh &&
              fcpo.purchaseOrder.pausedTimestamp === null &&
              !fcpo.purchaseOrder.closed,
          );
          mineFCPOs.forEach((fcpo) => {
            const downReport = reports.find(
              (r) => !r.available && r.site.id === fracMine.site.id && r.mesh && r.mesh.id === selectedMesh,
            );
            fracMinesWithPoInfo.push({
              ...fracMine,
              downReport,
              fcpo,
            });
          });
        });
        return fracMinesWithPoInfo.filter((fracMine) => validMineIds.includes(fracMine.id));
      }),
    );

    this.truckingVendors$ = this.currentFrac$.pipe(
      map((frac) => (frac.fracClusterTruckingVendors || []).filter((fctv) => fctv.status === 'awarded')),
      map((fctvs) =>
        fctvs.sort((a, b) => {
          if (a.truckingVendorId === 958) {
            return -1;
          } else if (b.truckingVendorId === 958) {
            return 1;
          } else {
            return a.truckingVendorName.localeCompare(b.truckingVendorName);
          }
        }),
      ),
    );

    this.trailerTypes$ = this.fracService.trailerTypes$;
  }

  public calculatePoRemaining(po: PurchaseOrder): number {
    const remaining = po.allocation - (po.pickedUp || 0);
    if (remaining < 0) {
      return 0;
    }
    return remaining;
  }

  private listenToDeadheadSearch() {
    combineLatest([
      this.newOrderForm.get('truckingVendorId').valueChanges,
      this.deadHeadSearchForm.valueChanges.pipe(
        filter((v) => !!v),
        startWith(null as string),
        debounceTime(200),
      ),
    ])
      .pipe(
        takeUntil(this.destroy$$),
        switchMap(([truckingVendor, searchTerm]) => {
          if (!truckingVendor) {
            return of([] as DeadheadLinkableOrder[]);
          }
          return this.orderService.getCancelledLoadsForDeadheadLinking$(truckingVendor, searchTerm);
        }),
      )
      .subscribe((value) => {
        this.deadheadLinkableOrders$$.next(value);
      });
  }
}
